import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import TableHorizontal, { InfoObject } from '../misc/TableHorizontal';
import { useSearchParams } from 'react-router-dom';
import { Row, Col, Input, Button, UncontrolledTooltip } from 'reactstrap';
import { CorePasswordAdminAccountRole } from '../../models/CorePasswordAdminModels';
import StatusCode from '../../util/StatusCode';
import Search from '../misc/Search';
import SearchableSelectInput from '../misc/SearchableSelectInput';
import { GetUsersForPasswordChange } from '../../store/PasswordChangeAction';
import { CoreDisplayableRef, PageResult } from '../../models/CoreModels';
import { CorePasswordChangeUser } from '../../models/CorePasswordChangeModels';
import { RequestState } from '../../models/RequestState';
import { RootState } from '../../store/store';
import FilterBottomComponent from '../misc/FilterBottomComponent';
import { debounce } from 'lodash';
import RequestParser from '../../util/RequestParser';
import { FlowSyncAccessPolicy } from '../../models/FlowSyncAccessPolicy';
import AuthorizedLink from '../auth/AuthorizedLink';
import { clearClassGroups, getClassGroups, getSchoolUnits } from '../../store/FilterAction';
import PasswordChangeModal from './PasswordChangeModal';
import { CorePasswordChangeRole } from '../../models/CoreInstanceSettings';

const PasswordChange = () => {

    const { t } = useTranslation(['common']);

    const dispatch = useDispatch();
    const [search, setSearch] = useState<string>('');
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedUser, setSelectedUser] = useState<CorePasswordChangeUser | undefined>(undefined);
    const [selectedUsers, setSelectedUsers] = useState<{ [key: string]: boolean }>({});
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const selectAllCheckboxRef = useRef<HTMLInputElement | null>(null);

    var filter = useMemo(() => RequestParser.parsePasswordChangeSearchPageRequest(searchParams), [searchParams]);

    const users = useSelector<RootState, RequestState<PageResult<CorePasswordChangeUser>>>(x => x.passwordChange.users);
    const schoolUnits = useSelector<RootState, CoreDisplayableRef[]>(x => x.filter.schoolUnits);
    const classGroups = useSelector<RootState, CoreDisplayableRef[]>(x => x.filter.classGroups);
    const accessRoleSetting = useSelector<RootState, CorePasswordChangeRole | undefined>(x => x.passwordChange.accessRole);

    const hasStudentAccess = accessRoleSetting !== undefined
        && (accessRoleSetting & CorePasswordChangeRole.STUDENT) === CorePasswordChangeRole.STUDENT;

    const hasStaffAccess = accessRoleSetting !== undefined
        && (accessRoleSetting & CorePasswordChangeRole.STAFF) === CorePasswordChangeRole.STAFF;

    const roleFilterItems = useMemo(() => {
        const items: { label: string, value: CorePasswordAdminAccountRole }[] = [];

        if (hasStaffAccess) {
            items.push({ label: t('password.roles.teacher', { ns: 'common' }), value: CorePasswordAdminAccountRole.STAFF });
        }

        if (hasStudentAccess) {
            items.push({ label: t('password.roles.student', { ns: 'common' }), value: CorePasswordAdminAccountRole.STUDENT });
        }

        return items;
    }, [hasStaffAccess, hasStudentAccess, t]);

    const isSingleRoleSetting = accessRoleSetting === CorePasswordChangeRole.STAFF
        || accessRoleSetting === CorePasswordChangeRole.STUDENT;

    const isRoleFilterLocked = isSingleRoleSetting;
    const lockedRoleValue = isSingleRoleSetting ? roleFilterItems[0].value : undefined;

    useEffect(() => {
        if (filter.search) {
            setSearch(filter.search);
        }
        GetUsersForPasswordChange(filter)(dispatch);
        getSchoolUnits()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        GetUsersForPasswordChange(filter)(dispatch);
        setSelectedUsers({});
        window.scrollTo(0, 0);
    }, [searchParams]);

    useEffect(() => {
        if (filter.schoolUnitId) {
            getClassGroups(filter.schoolUnitId)(dispatch);
        }
        else {
            clearClassGroups()(dispatch);
        }
    }, [dispatch, filter.schoolUnitId])

    const clearFilter = () => {
        setSearch('');
        setSearchParams([]);
    }

    const updateFilter = (name: string, value?: number | string | null) => {
        if (name === 'schoolUnitId') {
            searchParams.delete('classGroupId')
        }

        if (name !== 'page' && searchParams.has('page')) {
            searchParams.delete('page');
        }

        if (value !== undefined && value !== '' && value !== null) {
            if (typeof (value) === 'string') {
                searchParams.set(name, value);
            } else {
                searchParams.set(name, value.toString());
            }
        } else {
            searchParams.delete(name);
        }

        setSearchParams(searchParams);
    }

    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        let search = e.target.value;
        setSearch(search);
        debouncedSearch(search);
    };

    const debouncedSearch = useRef(
        debounce((search: string) => {
            updateFilter('search', search);
        }, 1000)
    ).current;

    const schoolUnitFilterItems = useMemo(() => {
        return schoolUnits.map(x => ({ value: x.id, label: x.title ?? "" }));
    }, [schoolUnits]);

    const classGroupFilterItems = useMemo(() => {
        return classGroups.map(x => ({ value: x.id, label: x.title ?? "" }));
    }, [classGroups]);

    const mapCoreDisplayableRef = (items: CoreDisplayableRef[], url: string) => {
        return items.reduce((acc, item, i) => {
            acc.push(<AuthorizedLink key={i} to={`/${url}/${item.id}`} accessPolicy={FlowSyncAccessPolicy.READ}>{item.title}</AuthorizedLink>);
            if (i < items.length - 1) acc.push(', ');
            return acc;
        }, [] as React.ReactNode[])
    }

    const openModal = (user: CorePasswordChangeUser) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    }

    const openBatchModal = () => {
        setSelectedUser(undefined);
        setIsModalOpen(true);
    }

    const selectedUsersList = (users.code === StatusCode.COMPLETE ? users.data.values : [])
        .filter(x => selectedUsers[x.id]);

    const selectedEnvironment = selectedUsersList.length > 0
        ? selectedUsersList[0].environment
        : undefined;

    const searchResultUsers = users.code === StatusCode.COMPLETE ? users.data.values : [];

    const allSearchResultsSameEnvironment = searchResultUsers.length > 0
        && searchResultUsers.every(user => user.environment === searchResultUsers[0].environment);

    const selectAllScopeUsers = selectedEnvironment
        ? searchResultUsers.filter(user => user.environment === selectedEnvironment)
        : searchResultUsers;

    const isAllSelectAllScopeUsersSelected = selectAllScopeUsers.length > 0
        && selectAllScopeUsers.every(user => selectedUsers[user.id]);

    const hasSelectedSearchResults = searchResultUsers.some(user => selectedUsers[user.id]);
    const isPartiallySelected = hasSelectedSearchResults && !isAllSelectAllScopeUsersSelected;

    useEffect(() => {
        if (selectAllCheckboxRef.current) {
            selectAllCheckboxRef.current.indeterminate = isPartiallySelected;
        }
    }, [isPartiallySelected]);

    const toggleAllSelected = () => {
        if (isAllSelectAllScopeUsersSelected) {
            setSelectedUsers({});
            return;
        }

        if (!selectedEnvironment && !allSearchResultsSameEnvironment) {
            return;
        }

        setSelectedUsers(
            selectAllScopeUsers.reduce((acc, user) => {
                acc[user.id] = true;
                return acc;
            }, {} as { [key: string]: boolean })
        );
    }

    const toggleSelected = (user: CorePasswordChangeUser) => {
        if (!selectedUsers[user.id] && selectedEnvironment && user.environment !== selectedEnvironment) {
            return;
        }

        setSelectedUsers(prev => ({
            ...prev,
            [user.id]: !prev[user.id]
        }));
    }

    const closeModal = () => {
        setIsModalOpen(false);
    }

    const tableContent = (): InfoObject[] => {
        if (users.code === StatusCode.COMPLETE) {

            const GetRoleName = (role: CorePasswordAdminAccountRole) => {
                if (role === CorePasswordAdminAccountRole.STAFF) {
                    return t('password.roles.teacher', { ns: 'common' });
                }
                else if (role === CorePasswordAdminAccountRole.STUDENT) {
                    return t('password.roles.student', { ns: 'common' });
                }
                return null;
            }

            
            return users.data.values.map(x => {
                const selectDisabled = !selectedUsers[x.id] && !!selectedEnvironment && x.environment !== selectedEnvironment;
                const selectTooltipId = `password-select-${x.id}`;

                return ({
                    id: x.id,
                    data: [
                        {
                            value: (
                                <>
                                    <span id={selectTooltipId} style={{ display: 'inline-block' }}>
                                        <Input
                                            type="checkbox"
                                            id={x.id}
                                            checked={selectedUsers[x.id] ?? false}
                                            disabled={selectDisabled}
                                            onChange={() => toggleSelected(x)}
                                        />
                                    </span>
                                    {selectDisabled && (
                                        <UncontrolledTooltip target={selectTooltipId} placement="right">
                                            {t('changePasswords.selectDisabledTooltip', { ns: 'common' })}
                                        </UncontrolledTooltip>
                                    )}
                                </>
                            )
                        },
                        { value: <AuthorizedLink to={`/user/${x.userId}`} accessPolicy={FlowSyncAccessPolicy.READ}>{x.userDisplayName}</AuthorizedLink> },
                        { value: x.environment },
                        { value: x.username },
                        { value: mapCoreDisplayableRef(x.schoolUnits, "schoolunit") },
                        { value: mapCoreDisplayableRef(x.classGroup, "group") },
                        { value: GetRoleName(x.role) },
                        {
                            value: (
                                <Button
                                    color='primary'
                                    style={{minWidth:'120px'}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openModal(x);
                                    }}
                                >
                                    {t('changePasswords.action.change', { ns: 'common' })}
                                </Button>
                            )
                        }
                    ]
                });
            })
        } else {
            return [];
        }
    }

    const selectAllDisabled = !isAllSelectAllScopeUsersSelected && !selectedEnvironment && !allSearchResultsSameEnvironment;
    const selectAllTooltipId = 'password-select-all';

    return (
        <div className="container">
            <div className="header--fixed large-filter">
                <h1>{t('changePasswords.title', { ns: 'common' })}</h1>
                <div>
                    <Row>
                        <Col sm="3">
                            <Search
                                value={search}
                                onChange={onSearch}
                                placeholder={t('password.search.name', { ns: 'common' })}
                            />
                        </Col>
                        <Col sm="3">
                            <SearchableSelectInput
                                items={schoolUnitFilterItems}
                                onChange={(name, value) => updateFilter(name, value)}
                                name='schoolUnitId'
                                selected={filter.schoolUnitId}
                                defaultText={t('password.search.chooseSchool', { ns: 'common' })}
                            />
                        </Col>
                        <Col sm="3">
                            <SearchableSelectInput
                                items={classGroupFilterItems}
                                onChange={(name, value) => updateFilter(name, value)}
                                name='classGroupId'
                                selected={filter.classGroupId}
                                disabled={classGroupFilterItems.length === 0}
                                defaultText={t('password.search.chooseClass', { ns: 'common' })}
                            />
                        </Col>
                        <Col sm="3">
                            <SearchableSelectInput
                                items={roleFilterItems}
                                onChange={(name, value) => updateFilter(name, value)}
                                name='role'
                                selected={lockedRoleValue ?? filter.role}
                                disabled={isRoleFilterLocked}
                                defaultText={t('password.search.chooseRole', { ns: 'common' })}
                            />
                        </Col>

                    </Row>
                    <Row>
                        <Col sm="12" className="toggle">
                            <Button
                                color='primary'
                                onClick={() => openBatchModal()}
                                disabled={selectedUsersList.length === 0}
                            >
                                {t('changePasswords.action.changeSelected', { ns: 'common', count: selectedUsersList.length })}
                            </Button>
                        </Col>
                    </Row>
                </div>
            </div>
            <div className="body--scroll padding-40">
                <PasswordChangeModal
                    isOpen={isModalOpen}
                    selectedUsers={selectedUser ? [selectedUser] : selectedUsersList}
                    selectedEnvironment={selectedUser?.environment ?? selectedEnvironment}
                    onClose={() => closeModal()}
                    onSuccess={() => GetUsersForPasswordChange(filter)(dispatch)}
                />
                <TableHorizontal
                    header={[
                        {
                            value: (
                                <>
                                    <span id={selectAllTooltipId} style={{ display: 'inline-block' }}>
                                        <Input
                                            type="checkbox"
                                            checked={isAllSelectAllScopeUsersSelected}
                                            disabled={selectAllDisabled}
                                            innerRef={selectAllCheckboxRef}
                                            onChange={toggleAllSelected}
                                        />
                                    </span>
                                    {selectAllDisabled && (
                                        <UncontrolledTooltip target={selectAllTooltipId} placement="right">
                                            {t('changePasswords.selectDisabledTooltip', { ns: 'common' })}
                                        </UncontrolledTooltip>
                                    )}
                                </>
                            )
                        },
                        { value: t('genericTableHeaders.name', { ns: 'common' }) },
                        { value: t('password.tableHeaders.environment', { ns: 'common' }) },
                        { value: t('password.tableHeaders.userName', { ns: 'common' }) },
                        { value: t('password.tableHeaders.school', { ns: 'common' }) },
                        { value: t('password.tableHeaders.class', { ns: 'common' }) },
                        { value: t('password.tableHeaders.role', { ns: 'common' }) },
                        { value: '' },
                    ]}
                    info={tableContent()}
                    error={users.code === StatusCode.ERROR}
                    searching={users.code === StatusCode.PENDING}
                    noResMsg={t('password.notFound.noRes', { ns: 'common' })}
                />
            </div>
            <FilterBottomComponent
                requestState={users}
                clearFilter={() => clearFilter()}
                updateFilter={(value) => updateFilter('page', value)}
            />
        </div>
    )
}

export default PasswordChange;
